import { useState } from "react";
import AppShell from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  classLabelFromKey,
  getSchoolState,
  getTeachers,
  updateAccountProfile,
  updateTeacherClass,
} from "@/lib/eisStore";
import {
  isSupabaseConfigured,
  updateTeacherClassInSupabase,
} from "@/lib/supabaseOps";

export default function TeacherData() {
  const school = getSchoolState();
  const classOptions = school.classes || [];
  const teachers = getTeachers();
  const [selectedEmail, setSelectedEmail] = useState("");
  const selectedTeacher = teachers.find((teacher) => teacher.email === selectedEmail) || null;
  const [form, setForm] = useState({
    name: "",
    email: "",
    assignedClassKey: "",
  });

  const pickTeacher = (teacher) => {
    setSelectedEmail(teacher.email);
    setForm({
      name: teacher.name || "",
      email: teacher.email || "",
      assignedClassKey: teacher.assignedClassKey || "",
    });
  };

  const save = async () => {
    if (!selectedTeacher) {
      toast.error("Please choose a teacher");
      return;
    }

    updateAccountProfile(selectedTeacher.email, "teacher", {
      ...selectedTeacher,
      name: form.name.trim() || selectedTeacher.name,
      email: form.email.trim() || selectedTeacher.email,
    });

    if (form.assignedClassKey !== selectedTeacher.assignedClassKey) {
      try {
        if (isSupabaseConfigured && selectedTeacher.id) {
          await updateTeacherClassInSupabase(selectedTeacher.id, form.assignedClassKey);
        }
      } catch (error) {
        console.error(error);
        toast.error(error?.message || "Could not save class to the database");
        return;
      }
      updateTeacherClass(form.email || selectedTeacher.email, form.assignedClassKey);
    }

    toast.success("Teacher details updated");
  };

  return (
    <AppShell>
      <div data-testid="teacher-data-page" className="space-y-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold" style={{ color: "#0b1f7a" }}>
            Teacher Data
          </h2>
          <p className="text-slate-500 mt-1 text-sm">
            View and manage all teacher records in the school.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-6">
          <Card>
            <CardHeader>
              <CardTitle style={{ color: "#0b1f7a" }}>All Teachers</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {teachers.length ? (
                teachers.map((teacher) => (
                  <button
                    key={teacher.email}
                    type="button"
                    onClick={() => pickTeacher(teacher)}
                    className={`w-full text-left rounded-xl border px-4 py-3 transition ${
                      selectedEmail === teacher.email
                        ? "border-[#0b1f7a] bg-[#EEF0FA]"
                        : "border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-800">{teacher.name}</p>
                        <p className="text-xs text-slate-500">{teacher.email}</p>
                      </div>
                      <span className="text-[10px] font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-600">
                        {teacher.assignedClassKey ? classLabelFromKey(teacher.assignedClassKey) : "Unassigned"}
                      </span>
                    </div>
                  </button>
                ))
              ) : (
                <p className="text-sm text-slate-500">No teachers found.</p>
              )}
            </CardContent>
          </Card>

          {selectedTeacher && (
            <Card>
              <CardHeader>
                <CardTitle style={{ color: "#0b1f7a" }}>Edit Teacher Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    value={form.email}
                    onChange={(e) => setForm((current) => ({ ...current, email: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Assigned Class</Label>
                  <Select
                    value={form.assignedClassKey}
                    onValueChange={(value) =>
                      setForm((current) => ({ ...current, assignedClassKey: value }))
                    }
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Assign class" />
                    </SelectTrigger>
                    <SelectContent>
                      {classOptions.map((item) => (
                        <SelectItem key={item.key} value={item.key}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex justify-end">
                  <Button
                    type="button"
                    onClick={save}
                    className="rounded-full text-white"
                    style={{ backgroundColor: "#0b1f7a" }}
                  >
                    Save Changes
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </AppShell>
  );
}
